<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { DraggableModal } from '@oat-sa-private/ui-components';
    import ScratchpadTools from './ScratchpadTools.svelte';
    import { __, getPointerEventCoords, generateElementId, getDefaultRemSizePx } from '@oat-sa-private/ui-core';
    import { testLayoutStore, setActiveTool } from '../../../layout/testLayoutStore.js';
    import { move, getShapePositionDelta, getShapeBBoxDelta } from './util/geometry.js';
    import { mount, unmount } from 'svelte';
    import { cloneDeep } from 'lodash';
    import Shape from './Shape.svelte';
    import Text from './Text.svelte';

    /**
     * Component is used to display scratchpad tools and drawings inside the DraggableModal
     */

    const creativeTools = ['rectangle', 'oval', 'line', 'brush'];

    /**
     * @typedef {Object} Props
     * @property {Number} top - top offset of the containing DraggableModal (px)
     * @property {Number} left - left offset of the containing DraggableModal (px)
     * @property {Number} width - width of DraggableModal (rem)
     * @property {Number} height - height of DraggableModal (rem)
     * @property {Object[]} [shapes] - to be displayed in svg container
     * @property {Object[]} tools - tools state
     * @property {Number} [canvasStateStackLength] - current canvas states number
     * @property {Number} [canvasStateIndex] - current canvas state index
     * @property {Boolean} [enableStateActions] - determines if state actions are enabled
     * @property {Function} onClose
     * @property {Function} onResize
     * @property {Function} onMove
     * @property {Function} onUndo
     * @property {Function} onRedo
     * @property {Function} onToolSelect
     * @property {Function} onChange
     * @property {Function} onUpdateState
     */
    /** @type {Props} */
    let {
        top = $bindable(),
        left = $bindable(),
        width = $bindable(),
        height = $bindable(),
        shapes = $bindable([]),
        tools = $bindable(),
        canvasStateStackLength = 0,
        canvasStateIndex = 0,
        enableStateActions = false,
        onClose,
        onResize,
        onMove,
        onUndo,
        onRedo,
        onToolSelect,
        onChange,
        onUpdateState
    } = $props();

    const canvasViewBox = {
        x: 0,
        y: 0,
        width: 3840,
        height: 2160
    };

    //minimum height of the DraggableModal = options height + header height
    let minHeight = $state();

    //keep some information while moving shapes
    const movement = $state({
        moving: false,
        key: false,
        viewBox: { x: 0, y: 0 },
        delta: { x: 0, y: 0 }
    });

    let drewShape;
    let drawArea = $state();
    let shapeContainer = $state();
    let currentTool = $state();

    /**
     * Handler for Tool selection event
     * @param {CustomEvent} e
     */
    function handleToolSelection(e) {
        tools = e.tools;
        onToolSelect?.({ tools });
        currentTool = getCurrentTool();
        onCurrentToolChange(currentTool);
    }

    /**
     * Current active tool watcher
     * @param {Object} activeTool - current active tool
     */
    function onCurrentToolChange(activeTool) {
        setActiveTool('scratchpad');
        if (!activeTool) {
            return;
        }
        shapes.forEach(shape => {
            if (shape.type === 'text') {
                shape.editable = activeTool.type === 'text';
            }
            shape.hoverable = ['eraser', 'select'].includes(activeTool.type);
            shape.selected = false;
        });
        redrawShapes();
        triggerChange();
    }

    /**
     * Handler for drawing area mousedown event
     * @param {MouseEvent} e event
     */
    function handlePointerDown(e) {
        setActiveTool('scratchpad');
        //act only if tool selected or drawing is not in progress
        if (currentTool && !drewShape) {
            //if we attempting to draw something
            if (creativeTools.includes(currentTool.type)) {
                unselectShapes();
                //check we are operating with left mouse button or single touch
                if (e.buttons === 1 || (e.changedTouches && e.changedTouches.length === 1)) {
                    const drawingGeometry = getDrawingGeometry(e, drawArea);
                    if (currentTool.options && currentTool.options.size) {
                        drawingGeometry.size = currentTool.options.size;
                    }
                    // FIXME: 'onFinishDraw' is firing even there's nothing has been drawn
                    const onFinishDraw = finishDrawEvt => {
                        if (finishDrawEvt) {
                            shapes.push(
                                Object.assign(finishDrawEvt, {
                                    key: generateElementId(currentTool.type),
                                    selected: true
                                })
                            );
                        }
                        redrawShapes();
                        triggerUpdate();
                        triggerChange();
                        unmount(drewShape);
                        drewShape = null;
                    };

                    drewShape = mount(Shape, {
                        target: drawArea,
                        props: {
                            drawingGeometry,
                            type: currentTool.type,
                            onFinishDraw
                        }
                    });
                }
            }
        }
    }

    /**
     * Gets the initial coordinates from event and drawing area
     * @param {PointerEvent} e pointer event
     * @param {DOMElement} area draw area
     * @returns {Object} drawingGeometry object
     */
    function getDrawingGeometry(e, area) {
        const { x, y } = getPointerEventCoords(e);
        const { x: drawAreaX, y: drawAreaY } = area.getBoundingClientRect();
        const startX = x - drawAreaX;
        const startY = y - drawAreaY;
        return {
            drawAreaStartX: startX,
            drawAreaStartY: startY,
            initialPointerX: x,
            initialPointerY: y
        };
    }

    /**
     * Handles click on drawing area
     * @param {PointerEvent} e event
     */
    function handleClick(e) {
        if (currentTool) {
            if (currentTool.type === 'text') {
                const drawingGeometry = getDrawingGeometry(e, drawArea);
                const key = generateElementId('text');
                const currentText = mount(Text, {
                    target: drawArea,
                    props: {
                        key,
                        drawingGeometry,
                        onFinishEditing: ev => {
                            shapes.push({ key, type: 'text' });
                            unmount(currentText);
                            handleFinishEditing(ev);
                        },
                        onEditing: handleTextEditing
                    }
                });
            } else if (currentTool.type === 'select' && e.target.closest('.shape-container') === null) {
                unselectShapes();
            }
        }
    }

    /**
     * Handler for `finishEditing` event
     * @param {CustomEvent} e event from Text component
     */
    function handleFinishEditing(e) {
        if (e.key) {
            const editedText = findShapeByKey(e.key);
            if (typeof e.content !== 'undefined') {
                editedText.content = e.content;
                editedText.geometry = e.geometry;
            }

            shapes.forEach(shape => (shape.selected = false));
            shapes = shapes.filter(shape => shape.type !== 'text' || (shape.content && shape.content.length));
            triggerUpdate();
            triggerChange();
        }
    }

    /**
     * Used to fire change while editing text
     * @param {Object} shape
     */
    function handleTextEditing(shape) {
        if (shape.key) {
            const editedText = findShapeByKey(shape.key);
            if (shape.content) {
                if (editedText) {
                    Object.assign(editedText, shape);
                    triggerChange();
                } else {
                    //while editing the manually created component, we don't change the reference of 'shapes'
                    onChange?.({
                        shapes: [...shapes, Object.assign({}, shape, { type: 'text' })]
                    });
                }
            }
        }
    }

    /**
     * Finds selected tool in tools
     * @returns {Object}
     */
    function getCurrentTool() {
        if (tools) {
            return tools.find(tool => tool.selected);
        } else {
            return null;
        }
    }

    /**
     * Sets select prop to false to all shapes
     */
    function unselectShapes() {
        shapes.forEach(shape => {
            shape.selected = false;
        });
        redrawShapes();
        triggerChange();
    }

    /**
     * Handler for undo header action button
     */
    function handleUndo() {
        onUndo?.();
    }

    /**
     * Handler for redo header action button
     */
    function handleRedo() {
        onRedo?.();
    }

    /**
     * Handler for DraggabelModal resize event
     * @param {CustomEvent} e event
     */
    function handleResize(e) {
        if (e.detail) {
            width = e.detail.width;
            height = e.detail.height;
            top = e.detail.top;
            left = e.detail.left;
            onResize?.({ width, height, top, left });
        }
    }

    /**
     * @param {CustomEvent} e finishResizing event
     * @param {Object} shape shape had being resized
     */
    function handleShapeFinishResizing(e, shape) {
        shape.geometry = e.geometry;
        redrawShapes();
        triggerUpdate();
        triggerChange();
    }

    /**
     * Handler for DraggableModal move event
     * @param {CustomEvent} e event
     */
    function handleMove(e) {
        onMove?.({ top: e.detail.top, left: e.detail.left });
    }

    /**
     * Handler for close button
     */
    function handleClose() {
        onClose?.();
    }

    /**
     * Handles header action
     * @param {CustomEvent} e
     */
    function handleModalAction(e) {
        const { key } = e.detail;
        switch (key) {
            case 'undo':
                handleUndo();
                break;

            case 'redo':
                handleRedo();
                break;
        }
    }

    /**
     * Handles ScratchpadTools mount
     * @param {Object} detail
     */
    function handleToolsMounted(detail) {
        const defaultPxInRem = getDefaultRemSizePx();
        const minHeightRem = detail.tools.length * (5.5 + 0.125) + 6.5;
        minHeight = minHeightRem * defaultPxInRem;
        currentTool = getCurrentTool();
        onCurrentToolChange(currentTool);
    }

    /**
     * Trigger reactive redraw of shapes
     */
    function redrawShapes() {
        shapes = shapes;
    }

    /**
     * Triggers the change event with the current shapes without selected, editable and hoverable properties
     */
    function triggerUpdate() {
        if (typeof onUpdateState !== 'function') {
            return;
        }
        /* eslint-disable no-unused-vars */
        onUpdateState({
            shapes: shapes.map(
                ({
                    selected,
                    editable,
                    hoverable,
                    drawingGeometry,
                    ...rest
                    // we have to make a clone of rest properties to unlink geometry property inside
                    // otherwise movement can affect previous states
                }) => cloneDeep(rest)
            )
        });
        /* eslint-enable no-unused-vars */
    }

    /**
     * Triggers the update event with the current shapes
     */
    function triggerChange() {
        onChange?.({
            shapes
        });
    }

    /**
     * Handles shape click
     * @param {Object} shape
     * @param {string} shape.key - the key of the clicked shape
     */
    function handleShapeClick({ key } = {}) {
        if (currentTool && currentTool.type === 'eraser') {
            shapes = shapes.filter(shape => shape.key !== key);
            triggerUpdate();
            triggerChange();
        }
    }

    /**
     * Mouse down on a shape
     * @param {MouseEvent} e
     * @param {Object} shape - the targeted shape
     * @param {string} shape.key - the key of the shape
     * @param {string} shape.type - the shape type
     * @param {Object} shape.geometry - the geometry of the shape
     */
    function handleShapeMouseDown(e, { key, type, geometry } = {}) {
        if (currentTool && currentTool.type === 'select') {
            // FIXME: this results in registering movement in handleMouseRelease even if shape wasn't moved
            if (!movement.moving) {
                movement.moving = true;
                movement.key = key;

                //the delta between the mouse and the shape coords
                const { x: deltaX, y: deltaY } = getShapePositionDelta(geometry, e.clientX, e.clientY);
                movement.delta = {
                    x: deltaX,
                    y: deltaY
                };

                //get the delta between the shape and it's bbox (a few pixels due to border or stroke size)
                let shapeBBox = {};
                if (type === 'text') {
                    const containerBbox = shapeContainer.getBoundingClientRect();
                    const bbox = e.target.getBoundingClientRect();
                    shapeBBox.x = bbox.x - containerBbox.x;
                    shapeBBox.y = bbox.y - containerBbox.y;
                } else {
                    let boundingElt = e.target;
                    if (!boundingElt.classList.contains('bounding')) {
                        boundingElt = boundingElt.closest('.shape-container').querySelector('.bounding');
                    }
                    if (boundingElt) {
                        shapeBBox = boundingElt.getBBox();
                    }
                }

                //apply the gap to the viewBox to constraints
                const { x: gapX, y: gapY } = getShapeBBoxDelta(geometry, shapeBBox.x, shapeBBox.y);
                movement.viewBox = {
                    x: canvasViewBox.x - gapX,
                    y: canvasViewBox.y - gapY,
                    width: canvasViewBox.width + gapX,
                    height: canvasViewBox.height + gapY
                };

                shapes.forEach(shape => {
                    if (shape.key === key) {
                        shape.selected = true;
                    } else {
                        shape.selected = false;
                        shape.hoverable = false;
                    }
                });
                redrawShapes();
                triggerChange();
            }
        }
    }

    /**
     * Release the mouse after a move
     */
    function handleMouseRelease() {
        if (movement.moving) {
            movement.moving = false;
            movement.key = false;
            shapes.forEach(shape => {
                shape.hoverable = false;
            });
            redrawShapes();
            triggerUpdate();
            triggerChange();
        }
    }

    /**
     * Moving shapes
     * @param {MouseEvent} e
     */
    function handleMouseMove(e) {
        if (movement.moving !== false) {
            e.preventDefault();
            e.stopPropagation();
            shapes.forEach(shape => {
                if (shape.key === movement.key) {
                    shape.geometry = move(
                        shape.type,
                        shape.geometry,
                        e.clientX - movement.delta.x,
                        e.clientY - movement.delta.y,
                        movement.viewBox
                    );
                    shape.selected = true;
                    if (shape.type === 'text') {
                        shape.editable = false;
                    }
                }
            });
            shapes = [...shapes];
            triggerChange();
        }
    }

    /**
     * Returns state actions based on current canvas state stack length and index of current canvas state
     * @param {Number} currentCanvasStateStackLength
     * @param {Number} currentCanvasStateIndex
     * @returns {Object[]}
     */
    function getStateActions(currentCanvasStateStackLength, currentCanvasStateIndex) {
        const undoAction = {
            key: 'undo',
            label: __('undo'),
            ariaLabel: __('undo'),
            icon: 'revert-16',
            disabled: false
        };
        const redoAction = { key: 'redo', label: __('redo'), ariaLabel: __('redo'), icon: 'redo-16', disabled: false };
        if (currentCanvasStateIndex === 0) {
            undoAction.disabled = true;
        }
        if (currentCanvasStateIndex + 1 === currentCanvasStateStackLength) {
            redoAction.disabled = true;
        }

        return [undoAction, redoAction];
    }

    /**
     * Searches shape with the given key
     * @param {string} key shape key
     * @returns {object} shape (graphic shape or text)
     */
    function findShapeByKey(key) {
        return shapes.find(shape => shape.key === key);
    }

    let stateActions = $derived(enableStateActions ? getStateActions(canvasStateStackLength, canvasStateIndex) : []);
</script>

<style>
    .text-tool {
        cursor: text;
    }
    .eraser-tool {
        /*
                custom cursor is svg of eraser icon urlencoded with width and height set,
                two digits following url are cursor actual position on the image, `pointer` is fallback cursor type
            */
        cursor:
            url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' class='icon medium svelte-1xsi500' width='16' height='16' viewBox='0 0 16 16' aria-hidden='false'%3E%3Ctitle%3EEraser icon%3C/title%3E%3Cpath d='M14.73 5l-3.54-3.57a2 2 0 00-2.83 0L.59 9.21a2 2 0 000 2.79l3 3h11.07v-1H8.52l6.21-6.21a2 2 0 000-2.79zM14 7.09l-3.53 3.53-4.93-4.95 3.53-3.53a1 1 0 011.42 0L14 5.67a1 1 0 010 1.42z'%3E%3C/path%3E%3C/svg%3E")
                4 16,
            pointer;
    }
    .moving {
        cursor: move;
    }
    .scratchpad-container {
        /* typing in "contenteditable" element forcibly scroll svg by browser */
        /* "left", "top", "margins", "offset" or any other properties do not help */
        /* should be remove after autosroll implementation */
        overflow: clip;
        height: 100%;
        /* disable drag guestures for touch devices */
        touch-action: none;
        position: relative;
        z-index: calc(var(--layer-5) - 2);

        &.active {
            z-index: calc(var(--layer-5));
        }
    }

    svg {
        transform-origin: top left;
    }
</style>

<svelte:window onmouseup={handleMouseRelease} onmousemove={handleMouseMove} />

<DraggableModal
    title={__('Scratchpad')}
    {top}
    {left}
    {width}
    {height}
    {minHeight}
    noRemScaling={true}
    actions={stateActions}
    pointerDownCallback={() => setActiveTool('scratchpad')}
    on:close={handleClose}
    on:action={handleModalAction}
    on:resize={handleResize}
    on:move={handleMove}>
    {#snippet children({ transformScale })}
        <div
            class="scratchpad-container"
            class:active={$testLayoutStore.activeTool === 'scratchpad'}
            bind:this={shapeContainer}>
            <ScratchpadTools
                onSelect={handleToolSelection}
                onMounted={handleToolsMounted}
                {tools}
                pointerDownCallback={() => setActiveTool('scratchpad')} />
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <svg
                xmlns="http://www.w3.org/2000/svg"
                version="1.1"
                xmlns:xlink="http://www.w3.org/1999/xlink"
                xmlns:svgjs="http://svgjs.com/svgjs"
                width={canvasViewBox.width}
                height={canvasViewBox.height}
                viewBox="{canvasViewBox.x} {canvasViewBox.y} {canvasViewBox.width} {canvasViewBox.height}"
                class:text-tool={currentTool && currentTool.type === 'text'}
                class:eraser-tool={currentTool && currentTool.type === 'eraser'}
                class:moving={movement.moving}
                style={transformScale !== 1 ? `transform: scale(${1 / transformScale});` : ''}
                bind:this={drawArea}
                onmousedown={handlePointerDown}
                onclick={handleClick}
                ontouchstart={handlePointerDown}>
                {#each shapes as shape (shape)}
                    {#if shape.type === 'text'}
                        {#key shape.key + shape.geometry.x + shape.geometry.y}
                            <!-- we want Text to be remounted on move but not on input -->
                            <Text
                                {...shape}
                                onFinishEditing={handleFinishEditing}
                                onEditing={handleTextEditing}
                                onMousedown={e => handleShapeMouseDown(e, shape)}
                                onClick={() => handleShapeClick(shape)} />
                        {/key}
                    {:else}
                        {#key JSON.stringify(shape.geometry)}
                            <Shape
                                {...shape}
                                onMousedown={e => handleShapeMouseDown(e, shape)}
                                onClick={() => handleShapeClick(shape)}
                                onFinishResizing={e => handleShapeFinishResizing(e, shape)} />
                        {/key}
                    {/if}
                {/each}
            </svg>
        </div>
    {/snippet}
</DraggableModal>
